import { useCallback } from 'react';
import { Platform } from 'react-native';
import type { To } from '@react-navigation/native/lib/typescript/src/useLinkTo';

import {
  useSafeIsNavigationReady,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContentNavigation = (params: UseMenuContentNavigationParams) => {
  const { restaurantSlug, orderChannel, deliveryDetailsData } = params;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();
  const navigation = useSafeNavigation();
  const isNavigationReady = useSafeIsNavigationReady();
  const routeParams = useSafeRouteParams<MaybeParamsWithCategorySlug>();
  const { trackEvent } = useTelemetry();

  const defaultActiveTargetId = routeParams?.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const changeLocation = useCallback(() => {
    trackEvent({ name: 'menu.change_location' });

    if (isExpoRouterEnabled) {
      router?.navigate('/locations');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'MenuTab',
      params: { screen: 'Locations' },
    });
  }, [isExpoRouterEnabled, navigation, router, trackEvent]);

  const generateCustomizationLink = useCallback(
    (productSlug: string): To => {
      if (orderChannel === 'delivery') {
        return {
          screen: 'Modal',
          params: {
            screen: 'CustomizationDelivery',
            params: {
              productSlug,
              addressId: deliveryDetailsData?.addressId ?? '',
            },
          },
        };
      }

      return {
        screen: 'Modal',
        params: {
          screen: 'Customization',
          params: { productSlug, restaurantSlug },
        },
      };
    },
    [deliveryDetailsData?.addressId, orderChannel, restaurantSlug],
  );

  const generateCustomizationHref = useCallback(
    (productSlug: string): string => {
      if (orderChannel === 'delivery') {
        const addressId = deliveryDetailsData?.addressId ?? '';

        return `/delivery/${addressId}/${productSlug}`;
      }

      return `/${restaurantSlug}/${productSlug}`;
    },
    [deliveryDetailsData?.addressId, orderChannel, restaurantSlug],
  );

  const setTargetParam = useCallback(
    (targetSlug: string) => {
      if (Platform.OS !== 'web' || !isNavigationReady) return;

      if (isExpoRouterEnabled) {
        router?.setParams({ category: targetSlug });

        return;
      }

      navigation?.setParams({ category: targetSlug });
    },
    [isExpoRouterEnabled, isNavigationReady, navigation, router],
  );

  const openContactUs = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/chat');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Chat' });
  }, [isExpoRouterEnabled, navigation, router]);

  return {
    defaultActiveTargetId,
    changeLocation,
    generateCustomizationLink,
    generateCustomizationHref: isExpoRouterEnabled ? generateCustomizationHref : undefined,
    setTargetParam,
    openContactUs,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMenuContentNavigationParams = {
  restaurantSlug: string;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
};

type MaybeParamsWithCategorySlug = { category?: string } & Record<
  string,
  string | string[] | undefined
>;
